import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Grid, Stack, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { setIn } from 'formik';
import { reduce } from 'lodash/collection';
import { isEmpty } from 'lodash/lang';

import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';
import openKeepIsHidden from 'components/root/Container/Layout/Tasks/openKeepIsHidden';
import { useCurrentRoute } from 'components/root/router';

import { PatientTravelApi, TaskApi } from '../../../../../../api';
import Section from '../../../../../../common/data-display/Section/Section';
import NotificationManager from '../../../../../../common/notifications/NotificationManager';
import { ROLE_SITE_FINANCIAL_COORDINATOR, ROLE_TRAVEL_COORDINATOR } from '../../../../../../constants/userRoles';
import { userHasAccessAs } from '../../../../../../services/auth';
import { onRequestError } from '../../../../../../services/handlers';
import { NS_COMMON, NSC_PATIENT, NSPP_PATIENT, NSPP_TRAVEL } from '../../../../../../services/i18n/namespaces';
import { getPatientAgeFromDob, getPatientPreferredName } from '../../../../../../services/patient';
import { PatientInfoHeaderContext, PatientInfoHeaderProvider } from '../../PatientInfoHeaderContext';
import { TR_BOOKED, TR_PENDING, TRST_FLIGHT } from '../EncountersSection/TravelSection/travelConstants';
import { TAB_TRAVEL } from '../EncountersSection/useActiveTab';

import AddressValidator from './AddressFields/AddressValidator';
import MainSection from './MainSection/MainSection';
import PreferredDropOffAddress from './PreferredDropOffAddress/PreferredDropOffAddress';
import PreferredPickupAddress from './PreferredPickupAddress/PreferredPickupAddress';
import PreferredTravelDateAndTime from './PreferredTravelDateAndTime/PreferredTravelDateAndTime';
import useTravelRequestState from './useTravelRequestState/useTravelRequestState';
import { validationSchema } from './useTravelRequestState/validationSchema';

import './TravelRequest.scss';

function TravelRequestComponent({ travelPreferences, encounterInfo, travelRequest }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentRoute = useCurrentRoute();
  const { ssuPatientId, travelRequestId } = useParams();
  const { patientInfo } = useContext(PatientInfoHeaderContext);

  const [{ values, errors }, actions] = useTravelRequestState(travelRequest, encounterInfo, travelPreferences);

  const [saving, setSaving] = useState(false);

  const isEdit = !!travelRequest;
  const readOnly =
    [TR_PENDING, TR_BOOKED].includes(values.status) &&
    !userHasAccessAs([ROLE_SITE_FINANCIAL_COORDINATOR, ROLE_TRAVEL_COORDINATOR]);
  const parentRouterName = currentRoute?.parent?.name;
  const backPath = parentRouterName === 'Travel Requests' ? '../../../' : isEdit ? '../../' : '../';

  const navigateBack = useCallback(
    function() {
      navigate(
        {
          pathname: backPath,
          search: createSearchParams({
            encounterTab: TAB_TRAVEL
          }).toString()
        },
        { relative: 'path' }
      );
    },
    [navigate, backPath]
  );

  return (
    <>
      <PageInfoHeader
        objectRecordLabel={patientInfo?.name + getPatientPreferredName(patientInfo)}
        pageInfo={
          <PageInfoHeader.CollapsibleList>
            <PageInfoHeader.AdditionalInfo title={t(`${NSC_PATIENT}:patientId`)}>
              {patientInfo?.patientId}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo title={t(`${NSC_PATIENT}:subjectId`)}>
              {patientInfo?.subjectId || t(`${NSPP_PATIENT}:info.noSubjectId`)}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:dateOfBirth`)}>
              {patientInfo?.dob} ({getPatientAgeFromDob(patientInfo)})
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:study`)}>
              {patientInfo?.studyName}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:site`)}>
              {patientInfo?.siteName}
            </PageInfoHeader.AdditionalInfo>
          </PageInfoHeader.CollapsibleList>
        }
      />
      <div className="eds-travel-request">
        <Section>
          <Box sx={{ flexGrow: 1, pb: 3 }} className="eds-travel-request-container">
            <MainSection
              readOnly={readOnly}
              isEdit={isEdit}
              encounterInfo={encounterInfo}
              values={values}
              errors={errors}
              actions={actions}
              navigateBack={navigateBack}
              travelRequestId={travelRequestId}
              ssuPatientId={ssuPatientId}
            />
            {values.requestedServices.includes(TRST_FLIGHT) && (
              <PreferredTravelDateAndTime readOnly={readOnly} values={values} errors={errors} actions={actions} />
            )}
            <PreferredPickupAddress
              readOnly={readOnly}
              preferredPickupAddress={travelPreferences.preferredPickupAddress}
              values={values}
              errors={errors}
              actions={actions}
            />
            <PreferredDropOffAddress
              readOnly={readOnly}
              preferredPickupAddress={travelPreferences.preferredPickupAddress}
              values={values}
              errors={errors}
              actions={actions}
            />
            <Grid container spacing={2} alignItems="flex-end" sx={{ padding: '0 12px', pt: 3 }}>
              <Grid size="grow">
                <TextField
                  disabled={readOnly}
                  name="comments"
                  value={values.comments}
                  onChange={actions.handleChange}
                  fullWidth
                  label={t('comments')}
                  multiline
                  maxRows={4}
                  variant="standard"
                  sx={{ maxWidth: 400 }}
                  slotProps={{
                    htmlInput: { maxLength: 100 }
                  }}
                />
              </Grid>
              <Grid size="auto">
                <Stack spacing={2} direction="row">
                  <Button disabled={readOnly} variant="outlined" onClick={navigateBack}>
                    {t('CANCEL')}
                  </Button>
                  <Button disabled={readOnly || saving} variant="contained" color="primary" onClick={submitForm}>
                    {isEdit ? t('SAVE') : t(`${NSPP_TRAVEL}:travelRequests.submitRequest`)}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Section>
      </div>
      <AddressValidator values={values} setAddressFields={actions.setAddressFields} />
    </>
  );
  function submitForm() {
    setSaving(true);
    validationSchema.validate(values, { abortEarly: false }).then(
      function() {
        (isEdit
          ? PatientTravelApi.updatePatientTravelRequest(ssuPatientId, travelRequestId, { encounterInfo, ...values })
          : PatientTravelApi.createPatientTravelRequest(ssuPatientId, { encounterInfo, ...values })
        )
          .then(function() {
            navigateBack();
            NotificationManager.success('Travel request submitted successfully');
          })
          .catch(({ response }) => {
            if (response.data.message) {
              NotificationManager.error(response.data.message);
            } else {
              NotificationManager.error('Unable to add Travel Request');
            }
          })
          .finally(function() {
            setSaving(false);
          });
      },
      function({ inner }) {
        if (inner) {
          actions.setErrors(parseValidationErrors(inner));
        }
        setSaving(false);
      }
    );
  }
}

function parseValidationErrors(inner) {
  /*todo: reuse*/
  return reduce(
    inner,
    function(accumulator, { path, message }) {
      return setIn(accumulator, path, message);
    },
    {}
  );
}

export default function TravelRequest() {
  const [searchParams] = useSearchParams();
  const { patientId, ssuPatientId, travelRequestId } = useParams();
  const [state, setState] = useState({});
  const ssuPatientEncounterId = searchParams.get('ssuPatientEncounterId');
  const protocolEncounterKey = searchParams.get('protocolEncounterKey');
  const protocolSituationalEncounterId = searchParams.get('protocolSituationalEncounterId');
  const encounterLookUpId = ssuPatientEncounterId || protocolEncounterKey || protocolSituationalEncounterId;

  const getAllRelatedTasksForTravelRequest = useCallback(
    function() {
      TaskApi.getAllPatientTasksForCreatedTravelRequest(ssuPatientId, travelRequestId).then(({ data }) => {
        if (!isEmpty(data)) {
          openKeepIsHidden(data[0].taskId, data[0].ssuId);
        }
      });
    },
    [ssuPatientId, travelRequestId]
  );

  useEffect(
    function() {
      const promises = [];

      if (patientId) {
        promises.push(PatientTravelApi.getTravelPreferences(patientId));
      }

      if (ssuPatientId) {
        if (encounterLookUpId) {
          promises.push(PatientTravelApi.getEncounter(ssuPatientId, encounterLookUpId));
        }

        if (travelRequestId) {
          promises.push(PatientTravelApi.getPatientTravelRequest(ssuPatientId, travelRequestId));
          getAllRelatedTasksForTravelRequest();
        }
      }

      Promise.all(promises).then(function([res1, res2, res3]) {
        setState({
          travelPreferences: res1?.data || null,
          encounterInfo: res2?.data || null,
          travelRequest: res3 || null
        });
      }, onRequestError);
    },
    [patientId, ssuPatientId, encounterLookUpId, travelRequestId, getAllRelatedTasksForTravelRequest]
  );

  return (
    <PatientInfoHeaderProvider>
      {isEmpty(state) ? null : (
        <TravelRequestComponent
          travelPreferences={state.travelPreferences}
          encounterInfo={state.encounterInfo}
          travelRequest={state.travelRequest}
        />
      )}
    </PatientInfoHeaderProvider>
  );
}
